import { WidgetType } from "@codemirror/view";
import type { Root } from "react-dom/client";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { JsonTree } from "@magic-md/plugin-sdk";
import { t as translate } from "@magic-md/i18n";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── FenceBodyTree — React component (full JSON tree, for active fences) ── */

interface FenceBodyTreeProps {
    jsonText: string;
    kind: "config" | "output" | "inputs";
}

function FenceBodyTree({ jsonText, kind }: FenceBodyTreeProps) {
    let parsed: unknown;
    let parseError: string | null = null;
    try {
        parsed = JSON.parse(jsonText);
    } catch (err) {
        parseError = err instanceof Error ? err.message : String(err);
    }

    if (parseError || parsed === undefined) {
        return (
            <div className="cm-magic-fence-body-fallback select-text font-mono text-[12px] px-3 py-2 text-muted-foreground">
                {parseError ? (
                    <span className="text-red-400">
                        {tr("syntax.fence.jsonParseFailed", { message: parseError })}
                    </span>
                ) : (
                    <span>{jsonText.slice(0, 200) || "\u00a0"}</span>
                )}
            </div>
        );
    }

    return (
        <div className="cm-magic-fence-body-tree" data-fence-kind={kind}>
            <div className="px-3 py-2 max-h-[480px] overflow-auto">
                <JsonTree
                    value={parsed}
                    defaultExpandDepth={1}
                    arrayPreviewLimit={50}
                />
            </div>
        </div>
    );
}

/* ── FenceBodyWidget — CodeMirror WidgetType ── */

export class FenceBodyWidget extends WidgetType {
    private root: Root | null = null;

    constructor(
        readonly jsonText: string,
        readonly kind: "config" | "output" | "inputs",
    ) {
        super();
    }

    eq(other: FenceBodyWidget): boolean {
        return other.jsonText === this.jsonText && other.kind === this.kind;
    }

    toDOM(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.className = "cm-magic-fence-body";
        wrapper.addEventListener("dblclick", (e) => {
            e.preventDefault();
            e.stopPropagation();
            window.dispatchEvent(
                new CustomEvent("magic-md:enter-syntax-edit", {
                    detail: { target: wrapper },
                }),
            );
        });
        this.root = createRoot(wrapper);
        this.root.render(
            createElement(FenceBodyTree, {
                jsonText: this.jsonText,
                kind: this.kind,
            }),
        );
        return wrapper;
    }

    updateDOM(_dom: HTMLElement): boolean {
        return false;
    }

    destroy(): void {
        const root = this.root;
        this.root = null;
        if (!root) return;
        queueMicrotask(() => {
            root.unmount();
        });
    }

    ignoreEvent(): boolean {
        return true;
    }
}
